import { lazy, Suspense, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, CalendarRange, Gift, TrendingDown, Trophy,
  // WAYS_TO_EARN tile icons — distinct from the badge grid which
  // now renders via BadgeIcon's own registry.
  Zap, Award, Users,
} from 'lucide-react';

/**
 * Lazy-loaded so lottie-react ships in its own chunk (shared
 * with ProfilePage's import). Per-call Suspense boundaries
 * below use the Lucide fallback as the suspense fallback —
 * during the chunk's first-open fetch the page renders
 * exactly as before AnimatedIcon was wired in.
 */
const AnimatedIcon = lazy(() => import('@/components/ui/AnimatedIcon'));

/**
 * Per-card wrapper for the Ways to Earn grid. Plays on mount
 * with a staggered delay (driven by the card's index in
 * WAYS_TO_EARN), on tap, and continuously loops via Lottie's
 * native loop mode from there. The four cards stay visibly
 * staggered because their initial play is offset (0/150/300/
 * 450ms); once each starts looping, they run independently and
 * never sync up.
 *
 * Size matches the redesigned vertical-stack card layout: the
 * 96px icon is the card's hero, with title/rate/desc reading
 * beneath as caption.
 */
function EarnCardIcon({
  name, fallback, delayMs,
}: { name: string; fallback: ReactNode; delayMs: number }) {
  return (
    <Suspense fallback={fallback}>
      <AnimatedIcon
        src={`/icons/${name}.json`}
        fallback={fallback}
        size={EARN_ICON_SIZE_PX}
        playOnMount
        playOnTap
        playDelay={delayMs}
        loop
      />
    </Suspense>
  );
}

/**
 * Trophy on the empty-state cards (badges or history). Loops
 * continuously via Lottie's native loop — empty states are a
 * "we want to draw your attention here" moment, so continuous
 * motion fits. No tap target since the surrounding card isn't
 * tappable either.
 *
 * 88px size — bigger than the prior 22px to actually anchor the
 * empty card visually. The flex-column-centered .emptyCard
 * layout absorbs the larger icon without changes.
 */
const EMPTY_TROPHY_SIZE_PX = 88;
export function EmptyStateTrophy() {
  const fallbackTrophy = (
    <Trophy
      size={EMPTY_TROPHY_SIZE_PX}
      strokeWidth={1.5}
      className={styles.emptyCardIcon}
    />
  );
  return (
    <Suspense fallback={fallbackTrophy}>
      <AnimatedIcon
        src="/icons/trophy.json"
        fallback={fallbackTrophy}
        size={EMPTY_TROPHY_SIZE_PX}
        playOnMount
        playOnTap={false}
        loop
      />
    </Suspense>
  );
}

/**
 * Gift on the Redeem CTA. Tap-only — the button label already
 * carries the call to action; an entry animation here would
 * compete with the label's "Redeem Points" copy and feel busy.
 * 14px to match the existing inline Gift size.
 */
function RedeemGiftIcon() {
  return (
    <Suspense fallback={<Gift size={14} />}>
      <AnimatedIcon
        src="/icons/gift.json"
        fallback={<Gift size={14} />}
        size={14}
        playOnMount={false}
        playOnTap
      />
    </Suspense>
  );
}
import type { BadgePublic, PointsHistoryEntry } from '@evi/shared';
import { fetchRedemptionConfig } from '@/lib/api';
import { StatusPill, TokenSpin } from '@/components/primitives';
import { BadgeIcon } from '@/components/ui/BadgeIcon';
import { useUserSummary } from '@/hooks/useGamification';
import { BirthdayRewardCard } from '@/components/rewards/BirthdayRewardCard';
import { RedemptionSheet } from './RedemptionSheet';
import styles from './RewardsPage.module.css';

interface Props { onNavigate: (path: string) => void }

// "Ways to earn" tiles — static marketing copy until a CMS feed exists.
// These aren't driven by the user-summary payload; they're the set of
// ways the platform *can* award points, shown regardless of current
// state. The Charge tile uses Zap (Lucide doesn't ship a `Bolt`
// glyph; the role is named `bolt` in public/icons/README.md to
// reflect the visual concept).
//
// `iconName` is the role key that AnimatedIcon resolves to a
// /icons/{name}.json file at render time. `icon` stays the
// Lucide component for the fallback path. All four cards pass
// the same size at the call site (EARN_ICON_SIZE_PX, 96).
//
// Rate strings are hardcoded — they encode the user-facing
// promise the tile makes about the platform's earning rules.
// Keeping them as literals (not settings-driven) decouples the
// marketing message from per-tenant settings drift; a tenant
// admin who raises `sessionCompletionBonus` from 5 to 7 on the
// Earning Rules page doesn't expect this UI surface to
// retroactively re-message "+7 pts" to every driver. The default
// values shipped here match the gamification engine defaults at
// packages/gamification/src/settings.ts (pointsPerKwh: 1,
// sessionCompletionBonus: 5, referralBonusReferrer: 100 → 25 per
// the Friends Stage 2 tier-1 milestone).
const WAYS_TO_EARN: Array<{
  iconName: string;
  icon: typeof Zap;
  title: string;
  rate: string;
  desc: string;
}> = [
  { iconName: 'bolt',     icon: Zap,     title: 'Charge',        rate: '+1 pt/kWh', desc: 'Every kWh you charge' },
  { iconName: 'award',    icon: Award,   title: 'Review',        rate: '+5 pts',    desc: 'Rate a destination' },
  { iconName: 'users',    icon: Users,   title: 'Refer',         rate: '+25 pts',   desc: 'Friend completes session' },
  // Phase 1 (2859eff) deleted the daily-app-open earning rule.
  // The actual flat per-session bonus (SESSION_BONUS event_type,
  // default +5 via settings.sessionCompletionBonus, fired
  // alongside the kWh-based SESSION_COMPLETED row at
  // packages/gamification/src/session-points.ts:302-319) has been
  // awarding correctly since launch. This tile replaces the
  // misleading "Daily" entry that lingered post-Phase-1 so Ways
  // to Earn surfaces what's actually awarded. Trophy icon
  // (trophy.json Lottie + Trophy Lucide fallback) reads as
  // "completion celebration" — distinct from Charge's bolt
  // semantic which is per-kWh, not per-session.
  { iconName: 'trophy',   icon: Trophy,  title: 'Session bonus', rate: '+5 pts',    desc: 'Complete a charge session' },
];

/** Stagger spacing for Ways-to-Earn entry animations (ms).
 *  The four cards' first-play moments are offset by this amount
 *  per index, so they don't all start in lockstep. With Lottie's
 *  native loop on, that offset persists naturally across cycles. */
const EARN_STAGGER_MS = 150;
/**
 * Side length (px) of the Ways-to-Earn icon. Anchors both the
 * AnimatedIcon size and the Lucide fallback so the layout stays
 * stable when JSON files are missing or in-flight.
 *
 * 96px is the visual hero of the card under the vertical-stack
 * layout. Going bigger (128px) starts pushing the card height
 * past comfortable thumb-scroll territory on narrow phones.
 */
const EARN_ICON_SIZE_PX = 96;

// Per-badge Lucide mapping moved into BadgeIcon (Phase 8 follow-up).
// The server now emits iconName + colorTier on each BadgePublic
// row; BadgeIcon resolves the Lucide component via its own
// registry and falls back to 'Award' on miss. This module no
// longer maintains an icon map.

/** Historical event-type enum → human label for the timeline row. */
export const EVENT_LABEL: Record<PointsHistoryEntry['eventType'], string> = {
  SESSION_COMPLETED:    'Charging session',
  // Distinct from SESSION_COMPLETED so the operator can scan a "you
  // got X for the kWh" row paired with a "and Y bonus" row below.
  SESSION_BONUS:        'Completion bonus',
  BADGE_EARNED:         'Badge earned',
  REFERRAL_BONUS:       'Referral bonus',
  // Friends Stage 2 — tier milestone fired when a referred friend
  // hit a configured session-count threshold (1/5/10 default).
  REFERRAL_MILESTONE:   'Friend milestone',
  DAILY_STREAK:         'Daily streak',
  MANUAL_ADJUSTMENT:    'Adjustment',
  REDEMPTION:           'Redemption',
  MIGRATION_CARRYOVER:  'Carryover',
  // Phase 4-6 event types.
  STREAK_CONTINUATION:  'Streak continuation',
  CHALLENGE_COMPLETED:  'Challenge completed',
  EXPIRATION:           'Points expired',
  UNLOCK_PURCHASE:      'Unlock purchase',
  // Phase 3 of gamification rules update — Google review CTA tap.
  REVIEW_AWARDED:       'Google review',
  // Gifting — the giver's debit and the receiver's credit rows.
  GIFT_SENT:            'Gift sent',
  GIFT_RECEIVED:        'Gift received',
  // Terms epic — one-time reward for turning on marketing consent.
  MARKETING_OPT_IN:     'Marketing opt-in',
  // Birthday reward — once-a-year claimed bonus.
  BIRTHDAY_BONUS:       'Birthday reward',
};

export function RewardsPage({ onNavigate }: Props) {
  const { t } = useTranslation('rewards');
  // Summary is the source of truth for rank / points / earned
  // badges / history. The full-catalog /badges query that used
  // to feed the locked-grid view was removed when this page
  // switched to earned-only — the locked silhouette spoiled the
  // discovery moments the badge engine is designed to deliver.
  // Operators still see the full catalog in the admin CMS.
  const summaryQ = useUserSummary();
  // Cached redemption config — drives the disabled-state copy of the
  // Redeem button without needing the full sheet to mount.
  const configQ = useQuery({
    queryKey: ['gamification', 'redemption-config'],
    queryFn: fetchRedemptionConfig,
    staleTime: 60_000,
  });
  const qc = useQueryClient();
  const [sheetOpen, setSheetOpen] = useState(false);

  const summary = summaryQ.data;

  if (summaryQ.isLoading && !summary) return <RewardsSkeleton onBack={() => onNavigate('/profile')} />;
  if (summaryQ.isError && !summary) {
    const msg = summaryQ.error instanceof Error ? summaryQ.error.message : t('error.loadFailed');
    return <RewardsError onBack={() => onNavigate('/profile')} message={msg} />;
  }
  if (!summary) return null;

  // Earned-only grid. Unearned badges are deliberately hidden so
  // the discovery moment is preserved when the user crosses a new
  // threshold — they see a NEW badge appear, not a previously-
  // visible silhouette flip from grey to color. summary.earnedBadges
  // already has the full BadgePublic shape (code, name, description,
  // icon, tier, pointsReward, earned: true) populated from the
  // user-summary API, so no mapping work is needed here.
  const badgeGrid: BadgePublic[] = summary.earnedBadges;

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => onNavigate('/profile')}>
        <ArrowLeft size={14} /> {t('back')}
      </button>

      {/* Hero — real points, real rank, real progress bar */}
      <section className={styles.hero}>
        <div className={styles.heroTopRow}>
          <span className={styles.heroEyebrow}>
            {t('hero.totalPoints')}
          </span>
          <span className={styles.heroPills}>
            {/* Weekly charging streak (Ticket 2) — hidden at 0,
                mirroring the friends-page flame convention.
                CalendarRange, not Flame: the flame is the DAILY
                streak's icon. The pill primitive already uppercases
                the copy and renders the count tabular-nums. */}
            {summary.stats.streakWeeks >= 1 && (
              <StatusPill tone="accent" variant="soft" size="sm">
                <CalendarRange size={11} />
                {t('hero.streak', { count: summary.stats.streakWeeks })}
              </StatusPill>
            )}
            <StatusPill tone="mute" variant="soft" size="sm">
              {summary.currentRank.name.toUpperCase()}
            </StatusPill>
          </span>
        </div>

        <div className={styles.pointsLine}>
          <TokenSpin size={52} />
          <span className={styles.pointsValue}>{summary.totalPoints.toFixed(2)}</span>
          <span className={styles.pointsUnit}>{t('hero.pointsUnit')}</span>
        </div>

        {summary.nextRank ? (
          <>
            <div className={styles.progressHeader}>
              <span className={styles.progressLabel}>
                <strong>{summary.pointsToNext.toFixed(0)}</strong> {t('progress.to')}{' '}
                <strong>{summary.nextRank.name.toUpperCase()}</strong>
              </span>
              <span className={styles.progressFraction}>
                {Math.round(summary.progressToNextPct)}%
              </span>
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${Math.max(2, summary.progressToNextPct)}%` }}
              />
            </div>
          </>
        ) : summary.ranksAvailable ? (
          // At the top rank — no "next rank" to show. Surface a congrats
          // line in the same position rather than leaving a gap. Reserved
          // for the ACTUAL top tier: ranksAvailable distinguishes it from
          // the unseeded-ranks state that used to congratulate a 0-point
          // user on maximum perks.
          <p className={styles.topRankLine}>
            {t('progress.topRank')}
          </p>
        ) : (
          // Ranks not configured (unseeded environment): a neutral line a
          // 0-point user can read honestly. Disappears once ranks seed.
          <p className={styles.topRankLine}>
            {t('progress.earnToClimb')}
          </p>
        )}

        {(() => {
          // Threshold reads from the live settings doc — admin can tune
          // the minimum without a code change. Falls back to 100 while
          // the config query is in-flight so the button isn't disabled
          // for a flash on first paint.
          const min = configQ.data?.minRedeemablePoints ?? 100;
          const eligible = summary.totalPoints >= min;
          return (
            <button
              type="button"
              className={styles.redeemBtn}
              disabled={!eligible}
              aria-disabled={!eligible}
              title={
                eligible
                  ? t('redeem.titleEligible')
                  : t('redeem.titleLocked', { count: Number((min - summary.totalPoints).toFixed(0)) })
              }
              onClick={() => { if (eligible) setSheetOpen(true); }}
            >
              <RedeemGiftIcon />
              {eligible
                ? t('redeem.cta')
                : t('redeem.more', { count: Number((min - summary.totalPoints).toFixed(0)) })}
            </button>
          );
        })()}
      </section>

      {/* Birthday reward — self-hides unless the driver is eligible to claim. */}
      <BirthdayRewardCard />

      {sheetOpen && (
        <RedemptionSheet
          totalPoints={summary.totalPoints}
          onClose={() => setSheetOpen(false)}
          onRedeemed={() => {
            qc.invalidateQueries({ queryKey: ['gamification'] });
          }}
        />
      )}

      {/* Ways to earn — marketing surface. Each card animates in
          on a 150ms stagger (card 0 = 0ms, 1 = 150ms, 2 = 300ms,
          3 = 450ms) and replays on tap. With no JSON files
          present each card renders its Lucide fallback and the
          stagger has no visible effect — that's the safe default. */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('earn.title')}</h2>
        <div className={styles.earnGrid}>
          {WAYS_TO_EARN.map(({ iconName, icon: Icon, title, rate }, index) => (
            <div key={title} className={styles.earnCard}>
              <span className={styles.earnIcon}>
                <EarnCardIcon
                  name={iconName}
                  fallback={<Icon size={EARN_ICON_SIZE_PX} strokeWidth={1.5} />}
                  delayMs={index * EARN_STAGGER_MS}
                />
              </span>
              <span className={styles.earnTitle}>{t(`earn.${iconName}.title`)}</span>
              <span className={styles.earnRate}>{rate}</span>
              <span className={styles.earnDesc}>{t(`earn.${iconName}.desc`)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Badges — earned only. Locked badges intentionally hidden
          so a freshly-earned badge is a discovery moment, not a
          silhouette flipping color. The total catalog count is
          also hidden — surfacing "of 67" anchors the user to a
          target they didn't pick and dilutes the milestone feel
          of any individual award. */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('badges.title')}</h2>
          <span className={styles.sectionCount}>
            {t('badges.earnedCount', { count: badgeGrid.length })}
          </span>
        </div>
        {badgeGrid.length === 0 ? (
          // Trophy renders here OR in the history empty state below
          // — never both at once (a user with zero history will
          // typically have zero badges, but each empty card is
          // gated by its own data shape, so the page is robust to
          // either alone). One Lottie file (`trophy.json`) covers
          // both sites.
          <div className={styles.emptyCard}>
            <EmptyStateTrophy />
            <p className={styles.emptyCardTitle}>{t('badges.emptyTitle')}</p>
            <p className={styles.emptyCardDesc}>
              {t('badges.emptyDesc')}
            </p>
          </div>
        ) : (
          <div className={styles.badgeGrid}>
            {badgeGrid.map((b) => (
              <BadgeCard key={b.code} badge={b} />
            ))}
          </div>
        )}
      </section>

      {/* Reward history — most recent 5 ledger entries with a
          link below to the full-history page (/rewards/history)
          where the same useUserSummary cache is consumed and
          the up-to-20 server slice is rendered in full alongside
          the earned-badges grid. Renamed from "History" → "Reward
          history" so the section reads cleanly on a screen that
          already has Charging History elsewhere in the menu —
          the noun-pair disambiguates which kind. */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('history.title')}</h2>
        {summary.recentHistory.length === 0 ? (
          // See note above: this Trophy and the badges-empty
          // Trophy are mutually exclusive in practice. Same
          // /icons/trophy.json drives both.
          <div className={styles.emptyCard}>
            <EmptyStateTrophy />
            <p className={styles.emptyCardTitle}>{t('history.emptyTitle')}</p>
            <p className={styles.emptyCardDesc}>
              {t('history.emptyDesc')}
            </p>
          </div>
        ) : (
          <>
            <div className={styles.historyList}>
              {summary.recentHistory.slice(0, 5).map((entry) => (
                <HistoryRow key={entry.id} entry={entry} />
              ))}
            </div>
            {/* "View all" footer link — surfaces only when there's
                history to view. Routes into /rewards/history which
                shows the full server slice (currently capped at
                the last 20 entries; pagination filed as polish). */}
            <button
              type="button"
              className={styles.viewAllLink}
              onClick={() => onNavigate('/rewards/history')}
            >
              {t('history.viewAll')}
            </button>
          </>
        )}
      </section>
    </div>
  );
}

// ── Inline components ────────────────────────────────────────────────

export function BadgeCard({ badge }: { badge: BadgePublic }) {
  // Earned-only grid (the page filters by earned before rendering),
  // so the previous earned/locked branching collapsed to a single
  // style. Defensive guard on the points-reward chip preserves
  // the "no chip when zero" existing behavior.
  //
  // BadgeIcon (commit feat(web,gamification): Lucide icons with
  // tier color coding) takes the per-badge iconName + colorTier
  // from the API, with built-in fallbacks when either is absent.
  return (
    <div
      className={`${styles.badgeCard} ${styles.badgeCardEarned}`}
      title={badge.description}
    >
      <span className={styles.badgeIconWrap}>
        <BadgeIcon
          iconName={badge.iconName}
          colorTier={badge.colorTier as 1 | 2 | 3 | 4 | undefined}
          size={48}
        />
      </span>
      <span className={styles.badgeName}>{badge.name}</span>
      {badge.pointsReward > 0 && (
        <span className={styles.badgeReward}>+{badge.pointsReward}</span>
      )}
    </div>
  );
}

export function HistoryRow({ entry }: { entry: PointsHistoryEntry }) {
  const { t } = useTranslation('rewards');
  const isEarn = entry.pointsDelta >= 0;
  const date = new Date(entry.createdAt);
  const dateLabel = date.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric',
  });
  return (
    <div className={styles.historyRow}>
      {isEarn ? (
        <span className={styles.historyIcon}>
          <TokenSpin size={24} />
        </span>
      ) : (
        <span className={`${styles.historyIcon} ${styles.iconDown}`}>
          <TrendingDown size={14} />
        </span>
      )}
      <div className={styles.historyInfo}>
        <p className={styles.historyLabel}>
          {entry.note ?? t(`event.${entry.eventType}`)}
        </p>
        <p className={styles.historySub}>
          {t(`event.${entry.eventType}`)} · {dateLabel}
        </p>
      </div>
      <StatusPill tone={isEarn ? 'good' : 'warn'} variant="soft" size="sm">
        {isEarn ? '+' : ''}{entry.pointsDelta.toFixed(2)}
      </StatusPill>
    </div>
  );
}

// ── Skeleton / error fallbacks ──────────────────────────────────────

function BadgeGridSkeleton() {
  return (
    <div className={styles.badgeGrid} aria-busy aria-live="polite">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className={styles.badgeCardSkeleton} />
      ))}
    </div>
  );
}

function RewardsSkeleton({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation('rewards');
  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={onBack}>
        <ArrowLeft size={14} /> {t('back')}
      </button>
      <div className={styles.heroSkeleton} />
      <div className={styles.gridSkeleton} />
      <BadgeGridSkeleton />
    </div>
  );
}

function RewardsError({ onBack, message }: { onBack: () => void; message: string }) {
  const { t } = useTranslation('rewards');
  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={onBack}>
        <ArrowLeft size={14} /> {t('back')}
      </button>
      <div className={styles.errorBox}>{message}</div>
    </div>
  );
}
